import React, { useEffect, useState } from "react";
import Parse from "../../api/ApiConfig";
import { Outlet, useNavigate, useParams } from "react-router";
import AdminDashboardLayout from "../../layout/AdminDashboardLayout";
import { PageHeader } from "./Apprenticeship.styled";
import FilterCategory from "../../components/ReusableComponents/FilterCategory";
import { Backdrop, Button, CircularProgress } from "@mui/material";
import { TabList1, TabList2 } from "./data";
import HeaderCharts from "./partials/HeaderCharts";
import AFDataTable from "./sections/AFDataTable";
import { fetchFundedData, fetchTableData } from "./utils";
import { toast } from "react-toastify";
import { StyledContainer } from "../../globalStyle/GlobalStyle";
import { canCreateAsset } from "../../utils/UserAuth";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveAdminQuickRequests, saveInvestorSponsoredRequests } from "./ApprenticeshipSlice";

/* eslint-disable no-unused-vars */
const Apprenticeship = () => {
	const dispatch = useDispatch();

	let navigate = useNavigate();
	let { category, id: requestId } = useParams();
	const [searchParams] = useSearchParams();

	const [currentTab, setCurrentTab] = useState(category || "ongoing");
	const [currentFilter, setCurrentFilter] = useState(getCurrentFilter(category, searchParams));
	const [TableData, setTableData] = useState([]);
	const [defaultData, setDefaultData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [fundedLoading, setFundedLoading] = useState(false);

	const getTableData = async (category, filter) => {
		const data = await fetchTableData(category || currentTab, filter || currentFilter);
		if (data) {
			setLoading(false);
			setTableData(data);
			setDefaultData(data);

			const quickFinanceRequests = data?.filter(
				(request) => request?.type === "quick" && request?.requestType === "old"
			);
			const sponsoredFinanceRequests = data?.filter(
				(request) => request?.type === "sponsored" && request?.requestType === "old"
			);

			dispatch(saveAdminQuickRequests(quickFinanceRequests));
			dispatch(saveInvestorSponsoredRequests(sponsoredFinanceRequests));
		} else {
			setLoading(false);
			setTableData([]);
			toast.error("Error fetching data!");
		}
	};

	useEffect(() => {
		// If current page is AF Dashboard, fetch data
		if (!requestId) {
			setLoading(true);
			getTableData();
		}
	}, []);

	useEffect(() => {
		if (category) {
			setCurrentTab(category);
		} else {
			navigate(`/apprenticeship/${currentTab}`, { replace: true });
		}
	}, [category]);

	const handleTabChange = (category, profile) => {
		setLoading(true);
		getTableData(category, profile);
		navigate(`/apprenticeship/${category}/?filter=${profile || currentFilter}`);
	};

	const handleProfileFilter = (profile) => {
		setCurrentFilter(profile);
		console.log(profile);
		const tabList =
			profile === "apprentice"
				? TabList1
				: profile === "investor"
				? TabList2
				: profile === "quick_finance"
				? TabList2
				: profile === "insurance"
				? [{ id: "completed" }]
				: TabList1;
		handleTabChange(tabList[0].id, profile);
	};

	const handleFilterChange = (e) => {
		setCurrentFilter(e.target.value);
		setLoading(true);
		getTableData(null, e.target.value);
	};

	const handleDeleteAF = async (selected) => {
		if (selected.length) {
			setLoading(true);
			const AFObjects = selected.map((item) => {
				const AFobj = new Parse.Object("ApprenticeshipFinance");
				AFobj.id = item.objectId;
				return AFobj;
			});

			Parse.Object.destroyAll(AFObjects)
				.then((object) => {
					getTableData();
					console.log("AF Item(s) deleted successfully", object);
					toast.success("Item(s) Deleted Successfully");
				})
				.catch((error) => {
					setLoading(false);
					console.error("Error while deleting Item(s)", error);
					toast.error("Error deleting Item(s)");
				});
		} else {
			toast.error("No Item selected");
		}
	};

	const getTabList = (filter) => {
		if (location.pathname.includes("/apprenticeship/insurance")) {
			return [];
		}
		if (filter === "apprentice") {
			return TabList1;
		} else if (filter === "investor") {
			return TabList2;
		} else if (filter === "quick_finance") {
			return TabList2;
		} else {
			return [];
		}
	};

	const fetchFundedRequests = async () => {
		try {
			setFundedLoading(true);
			const response = await fetchFundedData(searchParams.get("filter"));
			if (response) {
				setLoading(false);
				setTableData(response);
				setDefaultData(response);
			} else {
				setLoading(false);
				setTableData([]);
				toast.error("Error fetching data!");
			}
		} catch (error) {
			toast.error(error);
		} finally {
			setFundedLoading(false);
		}
	};

	useEffect(() => {
		if (location.pathname === "/apprenticeship/funded/") {
			fetchFundedRequests();
		} else {
			return;
		}
	}, [location.pathname]);

	return (
		<AdminDashboardLayout data={TableData} defaultData={defaultData} setData={setTableData}>
			<StyledContainer>
				<PageHeader>
					{!requestId && (
						<div className='topHeader flex gap-5'>
							<div className='flex scroll-wrapper' style={{ minWidth: "500px", overflowX: "auto" }}>
								<div
									className={`profileTypeBtn ${currentFilter === "apprentice" && "active"}`}
									onClick={() => handleProfileFilter("apprentice")}>
									Apprentice
								</div>

								<div
									className={`profileTypeBtn ${currentFilter === "investor" && "active"}`}
									onClick={() => handleProfileFilter("investor")}>
									Investor
								</div>

								<div
									className={`profileTypeBtn ${currentFilter === "quick_finance" && "active"}`}
									onClick={() => handleProfileFilter("quick_finance")}>
									Quick Apprentice Finance
								</div>
								<div
									className={`profileTypeBtn ${currentFilter === "insurance" && "active"}`}
									onClick={() => handleProfileFilter("insurance")}>
									Insurance
								</div>
							</div>
							{/* //edit */}
							{canCreateAsset() && currentFilter === "quick_finance" && (
								<Button
									variant='outlined'
									className='mr-3'
									onClick={() => navigate("/apprenticeship/create_request")}>
									Create Request
								</Button>
							)}
						</div>
					)}
					{!requestId && <HeaderCharts />}

					<FilterCategory
						currentTab={currentTab}
						tabList={getTabList(currentFilter)}
						handleClick={(category) => handleTabChange(category)}
					/>
				</PageHeader>

				<>
					{requestId ? (
						<Outlet context={[loading, setLoading]} />
					) : (
						<AFDataTable
							currentTab={currentTab}
							TableData={TableData}
							currentFilter={currentFilter}
							handleFilterChange={handleFilterChange}
							handleDeleteAF={handleDeleteAF}
						/>
					)}
				</>
			</StyledContainer>

			{loading && (
				<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
					<CircularProgress color='inherit' />
				</Backdrop>
			)}
			{fundedLoading && (
				<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={fundedLoading}>
					<CircularProgress color='inherit' />
				</Backdrop>
			)}
		</AdminDashboardLayout>
	);
};

export default Apprenticeship;

const getCurrentFilter = (category, searchParams) => {
	const filter = searchParams.get("filter");
	const filterList = ["apprentice", "investor", "quick_finance", "insurance"];
	const investorTabList = ["created_assets", "inactive", "active", "funded"];

	if (filter) {
		return filterList.includes(filter) ? filter : "apprentice";
	} else {
		return investorTabList.includes(category) ? "investor" : "apprentice";
	}
};
